import { useState, useCallback, useRef } from "react";

const DEFAULT_MAX_ITEMS = 50;

export function useChatInputHistory(maxItems = DEFAULT_MAX_ITEMS) {
  const [value, _setValue] = useState("");
  const valueRef = useRef("");
  const historyRef = useRef<string[]>([]);
  const indexRef = useRef(-1);
  const draftRef = useRef("");

  const setValue = useCallback((v: string | ((prev: string) => string)) => {
    if (typeof v === "function") {
      _setValue((prev) => {
        const next = v(prev);
        valueRef.current = next;
        return next;
      });
    } else {
      valueRef.current = v;
      _setValue(v);
    }
  }, []);

  const commit = useCallback(() => {
    const trimmed = valueRef.current.trim();
    if (!trimmed) return;

    const history = historyRef.current;
    if (history.length > 0 && history[history.length - 1] === trimmed) {
      // skip duplicate
    } else {
      history.push(trimmed);
      if (history.length > maxItems) {
        history.shift();
      }
    }

    indexRef.current = -1;
    draftRef.current = "";
    valueRef.current = "";
    _setValue("");
  }, [maxItems]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    const history = historyRef.current;
    if (history.length === 0) return;

    if (e.key === "ArrowUp") {
      if (indexRef.current === -1) {
        draftRef.current = valueRef.current;
        indexRef.current = history.length - 1;
      } else if (indexRef.current > 0) {
        indexRef.current--;
      }
      const v = history[indexRef.current];
      valueRef.current = v;
      _setValue(v);
    } else if (e.key === "ArrowDown") {
      if (indexRef.current === -1) return;
      if (indexRef.current < history.length - 1) {
        indexRef.current++;
        const v = history[indexRef.current];
        valueRef.current = v;
        _setValue(v);
      } else {
        indexRef.current = -1;
        valueRef.current = draftRef.current;
        _setValue(draftRef.current);
      }
    }
  }, []);

  return { value, setValue, onKeyDown, commit };
}
